import type { IdentifyHost } from './types.js';
import { broker } from '#~/agent/index.js';
import { logger } from '#~/logger/index.js';
import { classifyDiscoveryCause } from '../discovery-outcome.js';

/**
 * ENG-4927: asks one Bridge to identify the camera at `host` with its bounded
 * unicast probes (ONVIF GetDeviceInformation, VISCA CAM_VersionInq, PTZOptics
 * HTTP). A registry match surfaces as a discovery row at that host carrying
 * `brandId`/`modelId`; no match leaves state untouched (the Bridge never
 * guesses). Mirrors `discover.ts`: resolved `true` means the mutate was
 * delivered, not that the probe finished — the caller watches discovery rows.
 *
 * Transport failures resolve `false` instead of throwing: the caller's wait
 * window bounds the outcome either way, and a stale row from an earlier scan
 * may still answer. Per rule: settle-every-terminal-branch (ai.knowledge/global)
 * — every exit resolves. Only categorical fields are logged. See knowledge:
 * use-canonical-logger-not-console (ai.knowledge/studio.client).
 */
export const identifyHost: IdentifyHost = async (agentId, host): Promise<boolean> => {
  // E2E observation only (test-probes/identify-host-probe.ts): the global is
  // installed solely under IRIS_E2E_TEST, so this is a no-op in a user's app.
  (
    globalThis as { __irisIdentifyHostProbe?: { record?: (agentId: string, host: string) => void } }
  ).__irisIdentifyHostProbe?.record?.(agentId, host);

  try {
    const proxy = broker.acquire(agentId);

    if (!proxy) {
      logger.debug('identify-at-host skipped: selected Bridge is not ready', { agentId });
      return false;
    }

    // `Command.identify_host` (agent.proto field 4) — additive, old firmware ignores it.
    await proxy.mutate({ command: { identifyHost: host } }, {});

    return true;
  } catch (cause) {
    logger.debug('identify-at-host command failed', {
      agentId,
      outcome: classifyDiscoveryCause(cause).kind
    });

    return false;
  }
};
